import React, { useMemo, useRef, useState } from 'react';
import { useSnackbar } from 'notistack';
import { GridActionsCellItem, GridColDef, GridRenderCellParams, GridRowParams } from '@mui/x-data-grid-premium';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { AsyncDialog } from '../../components/AsyncDialog';
import { AsyncDialogRef } from '../../components/AsyncDialog/AsyncDialog';
import { ConfirmationDialog, ConfirmationDialogData } from '../../components/ConfirmationDialog';
import { Table } from '../../components/Table';
import { usePermissions } from '../../hooks';
import { useFetchAlpacaFeesQuery, useUpdateAlpacaFeeMutation } from '../../redux/features/alpacaFee/alpacaFee.apiSlice';
import { AlpacaFee } from '../../types/alpacaFee';
import * as permissions from '../../utils/permissions';
import { AlpacaFeeDialog, FEE_VALUE_UNIT } from './AlpacaFeeDialog';

/** A fee paused longer than this shows a red note, so a temporary pause (e.g. FINRA's $0 TAF) isn't forgotten. */
const PAUSED_WARNING_DAYS = 60;
const DAY_MS = 24 * 60 * 60 * 1000;

const pausedDays = (fee: AlpacaFee): number | null => {
  if (fee.isActive || !fee.pausedSince) {
    return null;
  }

  return Math.floor((Date.now() - new Date(fee.pausedSince).getTime()) / DAY_MS);
};

const showValue = (value: number | null) => (value === null || value === undefined ? '—' : String(value));

const describeSaveError = (error: any): string => {
  switch (error?.data?.message) {
    case 'ALPACA_FEE_PLATFORM_VALUE_BELOW_ALPACA_VALUE':
      return "Set our value (at least Alpaca's) before switching to it.";
    case 'ALPACA_FEE_NOT_FOUND':
      return 'This fee no longer exists.';
    default:
      return error?.data?.message || 'The fee could not be saved.';
  }
};

/**
 * The Alpaca fees users are charged (REG, TAF, CAT, international wire): pause / resume each one, choose our value or
 * Alpaca's, and edit the values. Every change is logged with the admin who made it.
 */
export function AlpacaFeeList() {
  const { enqueueSnackbar } = useSnackbar();
  const asyncDialogRef = useRef<AsyncDialogRef<ConfirmationDialogData, boolean>>(null);
  const { data: fees = [], isFetching, isError } = useFetchAlpacaFeesQuery();
  const [updateFee] = useUpdateAlpacaFeeMutation();
  const [editFee, setEditFee] = useState<AlpacaFee | null>(null);
  const canUpdate = usePermissions({ items: [permissions.AlpacaFee.update] });

  const confirmAndSave = async (
    fee: AlpacaFee,
    title: string,
    description: string,
    change: Partial<AlpacaFee>,
  ): Promise<void> => {
    await asyncDialogRef.current?.show({
      title,
      description,
      acceptButtonText: 'Confirm',
      onConfirm: async () => {
        try {
          await updateFee({ id: fee.id, ...change }).unwrap();
          enqueueSnackbar('Fee saved.', { variant: 'success' });
        } catch (err: any) {
          enqueueSnackbar(describeSaveError(err), { variant: 'error' });
          throw err;
        }
      },
    });
  };

  const onToggleActive = (fee: AlpacaFee) =>
    confirmAndSave(
      fee,
      fee.isActive ? `Pause ${fee.feeType}` : `Resume ${fee.feeType}`,
      fee.isActive
        ? `Users will pay 0 for ${fee.feeType} from now on. We keep paying Alpaca.`
        : `Users will pay ${fee.feeType} again from now on.`,
      { isActive: !fee.isActive },
    );

  const onToggleUsePlatform = (fee: AlpacaFee) =>
    confirmAndSave(
      fee,
      fee.usePlatformFeeValue ? `Charge Alpaca's value for ${fee.feeType}` : `Charge our value for ${fee.feeType}`,
      fee.usePlatformFeeValue
        ? `Users will pay Alpaca's value (${showValue(fee.alpacaFeeValue)}) from now on.`
        : `Users will pay our value (${showValue(fee.platformFeeValue)}) from now on.`,
      { usePlatformFeeValue: !fee.usePlatformFeeValue },
    );

  const columns = useMemo<GridColDef<AlpacaFee>[]>(() => {
    const base: GridColDef<AlpacaFee>[] = [
      { field: 'feeType', headerName: 'Fee', width: 210 },
      {
        field: 'isActive',
        headerName: 'Active',
        width: 230,
        sortable: false,
        renderCell: ({ row }: GridRenderCellParams<AlpacaFee>) => {
          const days = pausedDays(row);

          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Switch checked={row.isActive} disabled={!canUpdate} onChange={() => onToggleActive(row)} />
              {days !== null && (
                <Typography
                  variant="body2"
                  color={days > PAUSED_WARNING_DAYS ? 'error' : 'text.secondary'}
                  fontWeight={days > PAUSED_WARNING_DAYS ? 600 : 400}
                >
                  paused for {days} days
                </Typography>
              )}
            </Box>
          );
        },
      },
      {
        field: 'alpacaFeeValue',
        headerName: 'Alpaca value',
        width: 200,
        renderCell: ({ row }: GridRenderCellParams<AlpacaFee>) => (
          <Box>
            <Box component="code">{showValue(row.alpacaFeeValue)}</Box>
            <Typography variant="caption" color="text.secondary" display="block">
              {FEE_VALUE_UNIT[row.feeType]}
            </Typography>
          </Box>
        ),
      },
      {
        field: 'platformFeeValue',
        headerName: 'Our value',
        width: 140,
        renderCell: ({ value }) => <code>{showValue(value)}</code>,
      },
      {
        field: 'usePlatformFeeValue',
        headerName: 'Use our value',
        width: 130,
        sortable: false,
        renderCell: ({ row }: GridRenderCellParams<AlpacaFee>) => (
          <Switch checked={row.usePlatformFeeValue} disabled={!canUpdate} onChange={() => onToggleUsePlatform(row)} />
        ),
      },
      {
        field: 'alpacaMaxFeePerTrade',
        headerName: 'Alpaca cap',
        width: 110,
        renderCell: ({ value }) => showValue(value),
      },
      {
        field: 'platformMaxFeePerTrade',
        headerName: 'Our cap',
        width: 110,
        renderCell: ({ value }) => showValue(value),
      },
      { field: 'feeReference', headerName: 'Reference', flex: 1, minWidth: 200 },
    ];

    if (!canUpdate) {
      return base;
    }

    const actionColumn: GridColDef<AlpacaFee> = {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 80,
      getActions: ({ row }: GridRowParams<AlpacaFee>) => [
        <GridActionsCellItem
          key={`alpaca-fee-edit-${row.id}`}
          icon={<EditOutlinedIcon />}
          label="Edit values"
          onClick={() => setEditFee(row)}
        />,
      ],
    };

    return [actionColumn, ...base];
  }, [canUpdate]);

  return (
    <Container component="main" maxWidth="xl">
      <Box sx={{ pb: 5 }}>
        <Typography variant="h4">Alpaca Fees</Typography>
      </Box>

      {isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Could not load the Alpaca fees.
        </Alert>
      )}

      <AlpacaFeeDialog open={Boolean(editFee)} fee={editFee} onClose={() => setEditFee(null)} />

      <Table
        rows={fees}
        columns={columns}
        loading={isFetching}
        paginationMode="client"
        sortingMode="client"
        filterMode="client"
        initialSortModel={[{ field: 'feeType', sort: 'asc' }]}
        rowCount={fees.length}
        getRowHeight={() => 'auto'}
        wrapperHeight={420}
      />

      <AsyncDialog ref={asyncDialogRef} dialog={ConfirmationDialog} />
    </Container>
  );
}
